import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search, Loader2, Plus, Minus, Trash2, ArrowLeftRight, DollarSign,
  Package, ArrowRight, X, Image as ImageIcon, ScanLine, Award,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { gradeLabel } from "@/lib/grade";
import CheckoutModal from "@/components/CheckoutModal";
import QrScannerModal from "@/components/QrScannerModal";
import SlabModal from "@/components/SlabModal";

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];
import { useSubscription } from "@/context/SubscriptionContext";
const GAME_BADGE = { pokemon_japan: "JP", magic: "MTG", onepiece: "OP", riftbound: "RIFT", lorcana: "LOR", yugioh: "YGO" };
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const COND_MULT = { NM: 1.0, LP: 0.9, MP: 0.75, HP: 0.65, DMG: 0.5 };
const condMult = (c) => COND_MULT[c] ?? 1.0;
let trid = 0;

export default function Trade() {
  const qc = useQueryClient();
  const [tradeRate, setTradeRate] = useState(80);
  const [submitting, setSubmitting] = useState(false);
  const [paymentQrs, setPaymentQrs] = useState({});
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // LEFT: our cards leaving inventory
  const [sellQuery, setSellQuery] = useState("");
  const [sellResults, setSellResults] = useState([]);
  const [sellSearching, setSellSearching] = useState(false);
  const [sellCart, setSellCart] = useState([]); // {card, quantity}

  // RIGHT: customer cards coming in
  const [buyQuery, setBuyQuery] = useState("");
  const [buyResults, setBuyResults] = useState([]);
  const [buySearching, setBuySearching] = useState(false);
  const [showBuyResults, setShowBuyResults] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const { gameOptions } = useSubscription();
  const [buyItems, setBuyItems] = useState([]);
  const [overrideTotal, setOverrideTotal] = useState(""); // barter the trade-in credit
  const [overridePay, setOverridePay] = useState(""); // barter the final cash the customer pays
  const [overridePayout, setOverridePayout] = useState(""); // barter the cash we pay OUT to the customer
  const [roundUp, setRoundUp] = useState(false); // org's round-up-to-next-dollar setting
  const [sellScanOpen, setSellScanOpen] = useState(false);
  const [buySlabOpen, setBuySlabOpen] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => { setPaymentQrs(data.payment_qrs || {}); setRoundUp(!!data.price_round_up); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (sellQuery.trim().length < 2) { setSellResults([]); return; }
    const t = setTimeout(async () => {
      setSellSearching(true);
      try { const { data } = await api.get("/sell/search", { params: { q: sellQuery } }); setSellResults(data); }
      catch { /* ignore */ } finally { setSellSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [sellQuery]);

  useEffect(() => {
    if (buyQuery.trim().length < 2) { setBuyResults([]); return; }
    const t = setTimeout(async () => {
      setBuySearching(true);
      try { const { data } = await api.get("/catalog/search", { params: { q: buyQuery, limit: 20, category: catFilter } }); setBuyResults(data); setShowBuyResults(true); }
      catch { /* ignore */ } finally { setBuySearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [buyQuery, catFilter]);

  // LEFT handlers
  const addSell = useCallback((card) => {
    setSellCart((prev) => {
      const idx = prev.findIndex((l) => l.card.id === card.id);
      if (idx >= 0) {
        const next = [...prev];
        if (next[idx].quantity < (card.quantity || 0)) next[idx].quantity += 1;
        else toast.warning(`Only ${card.quantity} in stock`);
        return next;
      }
      return [...prev, { card, quantity: 1 }];
    });
    setSellQuery(""); setSellResults([]);
  }, []);
  const setSellQty = (id, q) => setSellCart((prev) => prev.map((l) => l.card.id === id
    ? { ...l, quantity: Math.max(1, Math.min(l.card.quantity || 1, q)) } : l));
  const removeSell = (id) => setSellCart((prev) => prev.filter((l) => l.card.id !== id));

  // Scan a card's QR sticker to add it to our-cards-out side.
  const onSellToken = useCallback(async (token) => {
    try {
      const { data } = await api.get(`/cards/by-qr/${encodeURIComponent(token)}`);
      if (!data.quantity || data.quantity <= 0) { toast.error(`${data.name} is out of stock`); return; }
      addSell(data);
      toast.success(`Added ${data.name} (${data.item_type === "slab" ? gradeLabel(data) : data.condition})`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No card matches that QR code"); }
  }, [addSell]);

  // RIGHT handlers
  const fetchLiveMarket = async (rid, productId, printing) => {
    try {
      const { data } = await api.get(`/catalog/live-price/${productId}`, { params: { printing: printing || "Normal", condition: "NM" } });
      const patch = { nm_lowest: data.lowest };
      if (data.market != null) { patch.market = data.market; patch.live = true; }
      updateBuy(rid, patch);
    } catch { /* keep catalog price */ }
  };
  const fetchCondOptions = async (rid, productId, printing, condition) => {
    try {
      const { data } = await api.get(`/catalog/live-price/${productId}`, { params: { printing: printing || "Normal", condition } });
      updateBuy(rid, { cond_median: data.median, cond_lowest: data.lowest,
        basis: data.median != null ? "median" : (data.lowest != null ? "lowest" : "median") });
    } catch { /* ignore */ }
  };
  const addBuy = (r) => {
    const printing = r.printings?.[0];
    const newRid = ++trid;
    setBuyItems((prev) => [...prev, {
      rid: newRid, product_id: r.product_id, name: r.name, set_name: r.set_name,
      number: r.number, rarity: r.rarity, image_url: r.image_url, category: r.category || "pokemon",
      printings: r.printings || [], printing: printing?.printing || "", market: printing?.market || 0,
      live: false, condition: "NM", qty: 1, basis: "market", boxed: r.boxed || {}, market_override: "", pct_override: "",
    }]);
    setBuyQuery(""); setShowBuyResults(false);
    fetchLiveMarket(newRid, r.product_id, printing?.printing || "Normal");
  };
  const updateBuy = (rid, patch) => setBuyItems((prev) => prev.map((it) => (it.rid === rid ? { ...it, ...patch } : it)));

  const addSlabBuy = (s) => {
    setBuyItems((prev) => [...prev, {
      rid: `slab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      is_slab: true, product_id: s.cert_number || `slab-${Date.now()}`,
      name: s.name, set_name: s.set_name, number: s.card_number, rarity: "",
      image_url: s.image_url, category: "pokemon",
      item_type: "slab", grade: s.grade, cert_number: s.cert_number, grading_company: "PSA",
      printings: [], printing: "", condition: "NM", qty: Number(s.quantity) || 1,
      basis: "market", boxed: {}, market: Number(s.market_value) || 0, market_override: "", pct_override: "",
    }]);
    toast.success(`Added slab ${s.grade || ""} ${s.name}`);
  };
  const removeBuy = (rid) => setBuyItems((prev) => prev.filter((it) => it.rid !== rid));
  const changePrinting = (it, value) => {
    const p = it.printings.find((x) => x.printing === value);
    updateBuy(it.rid, { printing: value, market: p?.market || 0, live: false });
    fetchLiveMarket(it.rid, it.product_id, value);
    if (it.condition !== "NM") fetchCondOptions(it.rid, it.product_id, value, it.condition);
  };
  const changeCondition = (it, value) => {
    if (value === "NM") updateBuy(it.rid, { condition: value, cond_median: null, cond_lowest: null, basis: "market" });
    else { updateBuy(it.rid, { condition: value, basis: "median" }); fetchCondOptions(it.rid, it.product_id, it.printing, value); }
  };
  const setBuyQty = (rid, q) => updateBuy(rid, { qty: Math.max(1, q) });

  const condMarket = (it) => {
    if (it.is_slab) return Number(it.market) || 0;
    if (it.condition === "NM") return Number(it.market) || 0;
    const chosen = it.cond_median != null ? it.cond_median : it.cond_lowest;
    if (chosen != null) return Number(chosen);
    return (Number(it.market) || 0) * condMult(it.condition);
  };
  // effective market = per-card override if set (and positive), else condition market
  const effMarket = (it) => {
    const o = Number(it.market_override);
    return (it.market_override !== "" && it.market_override != null && !Number.isNaN(o) && o > 0) ? o : condMarket(it);
  };
  // per-card trade % override (e.g. take one card at 85% while the rest are 80%)
  const effPct = (it) => {
    const p = Number(it.pct_override);
    return (it.pct_override !== "" && it.pct_override != null && !Number.isNaN(p) && p > 0) ? p : (Number(tradeRate) || 0);
  };
  const creditUnit = (it) => round2(effMarket(it) * effPct(it) / 100);

  // totals
  const ourTotal = round2(sellCart.reduce((s, l) => s + Number(l.card.tcg_price || 0) * l.quantity, 0));
  const computedTradeIn = round2(buyItems.reduce((s, it) => s + creditUnit(it) * (Number(it.qty) || 0), 0));
  const overrideVal = overrideTotal !== "" && Number(overrideTotal) > 0 ? round2(Number(overrideTotal)) : null;
  const tradeIn = overrideVal != null ? overrideVal : computedTradeIn;
  const difference = round2(ourTotal - tradeIn);
  // Round cash the same way as inventory (round UP to the next dollar) when the setting is on.
  const roundCash = (n) => roundUp ? Math.ceil(round2(n) - 1e-9) : round2(n);
  // Staff can override the final cash the customer pays (barter); otherwise it's the positive difference.
  const payOverrideVal = overridePay !== "" && Number(overridePay) >= 0 && !Number.isNaN(Number(overridePay)) ? round2(Number(overridePay)) : null;
  const customerPays = payOverrideVal != null ? payOverrideVal : (difference > 0 ? roundCash(difference) : 0);
  // Trades never give store credit: when the trade-in credit exceeds our cards, we pay the
  // difference out in CASH to the customer (rounded the same way when round-up is on). Staff can
  // override the payout when bartering (e.g. the customer asks for a bit more).
  const payoutOverrideVal = overridePayout !== "" && Number(overridePayout) >= 0 && !Number.isNaN(Number(overridePayout)) ? round2(Number(overridePayout)) : null;
  const cashToCustomer = difference < 0 ? (payoutOverrideVal != null ? payoutOverrideVal : roundCash(-difference)) : 0;

  const runTrade = async (method = "trade", app = "") => {
    setSubmitting(true);
    try {
      // If the staff bartered the trade-in to a flat number, scale each card's credit
      // proportionally so inventory cost accounting matches the agreed total.
      const scale = (overrideVal != null && computedTradeIn > 0) ? overrideVal / computedTradeIn : 1;
      const sell_items = sellCart.map((l) => ({ card_id: l.card.id, quantity: l.quantity }));
      const buy_items = buyItems.map((it) => it.is_slab ? {
        name: it.name, set_name: it.set_name, card_number: it.number,
        condition: "NM", quantity: Number(it.qty) || 1, image_url: it.image_url,
        category: "pokemon", item_type: "slab", grade: it.grade, cert_number: it.cert_number, grading_company: "PSA",
        tcg_product_id: it.product_id, sku: it.cert_number || it.product_id, tcg_printing: "",
        tcg_market_price: round2(effMarket(it)), tcg_price: round2(effMarket(it)),
        price_basis: it.market_override !== "" && it.market_override != null ? "override" : "",
        cost: round2(creditUnit(it) * scale), buy_pct: Number(effPct(it)), on_tcgplayer: false, price_override: true,
      } : {
        name: it.name, set_name: it.set_name, card_number: it.number, rarity: it.rarity,
        condition: it.condition, quantity: Number(it.qty) || 1, image_url: it.image_url,
        category: it.category || "pokemon", tcg_product_id: it.product_id, sku: it.product_id,
        tcg_printing: it.printing, tcg_market_price: round2(effMarket(it)), tcg_price: round2(effMarket(it)),
        price_basis: it.market_override !== "" && it.market_override != null ? "override"
          : (it.basis === "lowest" ? "lowest" : (it.basis === "median" ? "median" : "")),
        cost: round2(creditUnit(it) * scale), buy_pct: Number(effPct(it)), on_tcgplayer: true,
      });
      const { data } = await api.post("/trade/checkout", {
        sell_items, buy_items, trade_rate: Number(tradeRate) || 0,
        cash_difference: customerPays, payout_override: payoutOverrideVal,
        payment_method: method, payment_app: app, to_staging: true,
      });
      const msg = data.direction === "customer_pays" ? `Customer pays ${fmt(data.amount)}`
        : data.direction === "cash_to_customer" ? `Cash paid to customer ${fmt(data.amount)}` : "Even trade";
      toast.success(`Trade complete · ${msg}${data.staged ? ` · ${data.staged} card${data.staged > 1 ? "s" : ""} sent to Staging` : ""} (${data.order_ref})`);
      setSellCart([]); setBuyItems([]); setOverrideTotal(""); setOverridePay(""); setOverridePayout(""); setCheckoutOpen(false);
      qc.invalidateQueries({ queryKey: ["cards"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const onComplete = () => {
    if (sellCart.length === 0 && buyItems.length === 0) { toast.error("Add cards to the trade first"); return; }
    // Customer owes money -> collect payment first; otherwise complete immediately.
    if (customerPays > 0) setCheckoutOpen(true);
    else runTrade("trade");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="trade-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100 flex items-center gap-3">
          <ArrowLeftRight className="h-7 w-7 text-cyan-400" /> Trade
        </h1>
        <p className="text-sm text-slate-400 mt-1">Our cards go out on the left at retail; the customer's cards come in on the right, credited at your trade rate. The difference is what they pay us — or, if their cards are worth more, the cash we pay out to them.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: our cards out */}
        <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--pk-border)", background: "rgba(245,158,11,0.06)" }}>
            <DollarSign className="h-5 w-5 text-amber-400" />
            <div className="font-heading font-bold text-slate-100">Our cards out <span className="text-slate-500 font-normal text-sm">(retail · {sellCart.length})</span></div>
          </div>
          <div className="p-4">
            <div className="flex gap-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <Search className="h-4 w-4 text-slate-500" />
                <input value={sellQuery} onChange={(e) => setSellQuery(e.target.value)} placeholder="Search your inventory by name, SKU, set..."
                  data-testid="trade-sell-search" className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
                {sellSearching && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
              </div>
              <button onClick={() => setSellScanOpen(true)} data-testid="trade-sell-scan-btn" title="Scan a card's QR sticker to add it to our cards out"
                className="shrink-0 flex items-center gap-1.5 px-3 rounded-lg border text-sm font-semibold text-amber-300 hover:bg-amber-500/10" style={{ borderColor: "var(--pk-border)" }}>
                <ScanLine className="h-4 w-4" /> Scan
              </button>
            </div>
            {sellResults.length > 0 && (
              <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border divide-y" style={{ borderColor: "var(--pk-border)" }} data-testid="trade-sell-results">
                {sellResults.map((c) => (
                  <button key={c.id} onClick={() => addSell(c)} data-testid="trade-sell-result-item"
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--pk-hover)] text-left">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{c.set_name} · {c.item_type === "slab" ? gradeLabel(c) : c.condition} · {c.quantity} in stock</div>
                    </div>
                    <span className="font-mono-num text-sm text-amber-400">{fmt(c.tcg_price)}</span>
                    <Plus className="h-4 w-4 text-slate-500" />
                  </button>
                ))}
              </div>
            )}

            {sellCart.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-500">No cards selected from inventory yet.</div>
            ) : (
              <div className="mt-3 divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
                {sellCart.map((l) => (
                  <div key={l.card.id} data-testid="trade-sell-line" className="py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{l.card.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{l.card.set_name} · {l.card.item_type === "slab" ? gradeLabel(l.card) : l.card.condition} · list {fmt(l.card.tcg_price)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSellQty(l.card.id, l.quantity - 1)} className="h-7 w-7 rounded border flex items-center justify-center text-slate-400 hover:text-rose-400" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3 w-3" /></button>
                      <span className="w-6 text-center font-mono-num text-sm text-slate-100">{l.quantity}</span>
                      <button onClick={() => setSellQty(l.card.id, l.quantity + 1)} className="h-7 w-7 rounded border flex items-center justify-center text-slate-400 hover:text-emerald-400" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3 w-3" /></button>
                    </div>
                    <span className="w-20 text-right font-mono-num text-sm text-amber-400">{fmt(Number(l.card.tcg_price || 0) * l.quantity)}</span>
                    <button onClick={() => removeSell(l.card.id)} data-testid="trade-sell-remove" className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t flex items-center justify-between" style={{ borderColor: "var(--pk-border)" }}>
            <span className="text-sm text-slate-300">Our cards (retail)</span>
            <span className="font-mono-num text-lg font-bold text-amber-400" data-testid="trade-our-total">{fmt(ourTotal)}</span>
          </div>
        </div>

        {/* RIGHT: customer cards in */}
        <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="px-5 py-4 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--pk-border)", background: "rgba(16,185,129,0.06)" }}>
            <Package className="h-5 w-5 text-emerald-400 shrink-0" />
            <div className="font-heading font-bold text-slate-100 mr-auto">Cards we take in <span className="text-slate-500 font-normal text-sm">({buyItems.length})</span></div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 hidden sm:inline">Trade-in rate</span>
              <button onClick={() => setTradeRate((p) => Math.max(0, (Number(p) || 0) - 5))} data-testid="trade-rate-minus" className="h-7 w-7 rounded-lg border flex items-center justify-center text-slate-400 hover:text-cyan-400" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3 w-3" /></button>
              <div className="flex items-center gap-0.5 px-2 py-1 rounded-lg border w-16 focus-within:ring-2 focus-within:ring-cyan-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <input type="number" inputMode="numeric" value={tradeRate} min={0} max={100} onFocus={(e) => e.target.select()}
                  onChange={(e) => setTradeRate(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  data-testid="trade-rate-input" className="w-full bg-transparent outline-none text-sm font-bold text-cyan-400 font-mono-num text-center" />
                <span className="text-slate-500 text-xs">%</span>
              </div>
              <button onClick={() => setTradeRate((p) => Math.min(100, (Number(p) || 0) + 5))} data-testid="trade-rate-plus" className="h-7 w-7 rounded-lg border flex items-center justify-center text-slate-400 hover:text-cyan-400" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3 w-3" /></button>
            </div>
          </div>
          <div className="p-4">
            <div className="flex gap-2">
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 focus-within:ring-2 focus-within:ring-emerald-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <Search className="h-4 w-4 text-slate-500" />
                <input value={buyQuery} onChange={(e) => setBuyQuery(e.target.value)} placeholder="Search catalog for cards they're trading in..."
                  data-testid="trade-buy-search" className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
                {buySearching && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
              </div>
              <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="trade-buy-category"
                className="px-2 rounded-lg border text-xs text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                {gameOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
              <button onClick={() => setBuySlabOpen(true)} data-testid="trade-buy-slab-btn" title="Take in a graded slab by cert number or barcode"
                className="shrink-0 flex items-center gap-1.5 px-3 rounded-lg border text-sm font-semibold text-violet-300 hover:bg-violet-500/10" style={{ borderColor: "var(--pk-border)" }}>
                <Award className="h-4 w-4" /> Slab
              </button>
            </div>
            {showBuyResults && buyResults.length > 0 && (
              <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border divide-y" style={{ borderColor: "var(--pk-border)" }} data-testid="trade-buy-results">
                {buyResults.map((r) => (
                  <button key={r.product_id} onClick={() => addBuy(r)} data-testid="trade-buy-result-item"
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[var(--pk-hover)] text-left">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate flex items-center gap-1.5">{r.name}{GAME_BADGE[r.category] && <span className="shrink-0 px-1 py-0.5 rounded bg-indigo-950/80 border border-indigo-800/60 text-[9px] font-bold text-indigo-300">{GAME_BADGE[r.category]}</span>}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.set_name} · #{r.number} · {r.rarity}</div>
                    </div>
                    {r.boxed && Object.keys(r.boxed).length > 0 && (
                      <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-950/80 border border-blue-800/60 text-[9px] font-bold text-blue-400" title="Boxed.gg is buying this card">BOXED</span>
                    )}
                    {r.printings?.[0]?.market != null && <span className="font-mono-num text-sm text-slate-300">{fmt(r.printings[0].market)}</span>}
                    <Plus className="h-4 w-4 text-slate-500" />
                  </button>
                ))}
              </div>
            )}

            {buyItems.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-500">No trade-in cards added yet.</div>
            ) : (
              <div className="mt-3 divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
                {buyItems.map((it) => (
                  <div key={it.rid} data-testid="trade-buy-line" className="py-3 space-y-2">
                    {/* row 1: name + market + remove */}
                    <div className="flex items-start gap-3">
                      <div className="h-16 w-12 shrink-0 rounded-md border overflow-hidden flex items-center justify-center bg-[var(--pk-card)]" style={{ borderColor: "var(--pk-border)" }} data-testid="trade-buy-image">
                        {it.image_url ? <img src={it.image_url} alt={it.name} className="h-full w-full object-contain" loading="lazy" /> : <ImageIcon className="h-5 w-5 text-slate-700" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-100 truncate flex items-center gap-1.5">{it.name}{it.boxed && it.boxed[it.printing] && <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-950/80 border border-blue-800/60 text-[9px] font-bold text-blue-400" title="Boxed.gg is buying this card">BOXED</span>}</div>
                        <div className="text-[11px] text-slate-500 truncate">{[it.set_name, it.number ? `#${it.number}` : ""].filter(Boolean).join(" · ")}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Market{it.live ? " (live)" : ""}</div>
                        <div className="font-mono-num text-sm text-slate-200" data-testid="trade-buy-market">{fmt(condMarket(it))}</div>
                      </div>
                      <button onClick={() => removeBuy(it.rid)} data-testid="trade-buy-remove" className="text-slate-500 hover:text-rose-400 mt-0.5"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    {/* row 2: printing, condition, qty */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {it.printings.length > 1 && (
                        <select value={it.printing} onChange={(e) => changePrinting(it, e.target.value)} data-testid="trade-buy-printing"
                          className="px-2 py-1 rounded border text-xs text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                          {it.printings.map((p) => <option key={p.printing} value={p.printing}>{p.printing || "Normal"}</option>)}
                        </select>
                      )}
                      {it.is_slab ? (
                        <span data-testid="trade-buy-grade" className="px-2 py-1 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40 text-xs font-bold">{gradeLabel(it) || "Slab"}</span>
                      ) : (
                        <select value={it.condition} onChange={(e) => changeCondition(it, e.target.value)} data-testid="trade-buy-condition"
                          className="px-2 py-1 rounded border text-xs text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                          {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                      <div className="flex items-center gap-1 ml-auto">
                        <button onClick={() => setBuyQty(it.rid, (Number(it.qty) || 1) - 1)} className="h-7 w-7 rounded border flex items-center justify-center text-slate-400 hover:text-rose-400" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3 w-3" /></button>
                        <span className="w-6 text-center font-mono-num text-sm text-slate-100">{it.qty}</span>
                        <button onClick={() => setBuyQty(it.rid, (Number(it.qty) || 1) + 1)} className="h-7 w-7 rounded border flex items-center justify-center text-slate-400 hover:text-emerald-400" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3 w-3" /></button>
                      </div>
                    </div>
                    {/* row 3: per-card market + % overrides and resulting credit */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-[11px] text-slate-500">
                          <span className="w-16">Override mkt</span>
                          <div className="flex items-center gap-1 px-2 py-1 rounded border w-24 focus-within:ring-2 focus-within:ring-emerald-500/50" style={{ background: "var(--pk-card)", borderColor: it.market_override !== "" && it.market_override != null ? "rgba(16,185,129,0.5)" : "var(--pk-border)" }} title="Override the market value for this card — also becomes its inventory price">
                            <span className="text-slate-500 text-xs">$</span>
                            <input type="number" inputMode="decimal" min="0" value={it.market_override ?? ""} placeholder={condMarket(it).toFixed(2)} onFocus={(e) => e.target.select()}
                              onChange={(e) => updateBuy(it.rid, { market_override: e.target.value })} data-testid="trade-buy-market-override"
                              className="w-full bg-transparent outline-none text-xs text-slate-100 font-mono-num text-right" />
                            {it.market_override !== "" && it.market_override != null && (
                              <button onClick={() => updateBuy(it.rid, { market_override: "" })} className="text-slate-500 hover:text-rose-400"><X className="h-3 w-3" /></button>
                            )}
                          </div>
                        </label>
                        <label className="flex items-center gap-2 text-[11px] text-slate-500">
                          <span className="w-16">Override %</span>
                          <div className="flex items-center gap-1 px-2 py-1 rounded border w-24 focus-within:ring-2 focus-within:ring-cyan-500/50" style={{ background: "var(--pk-card)", borderColor: it.pct_override !== "" && it.pct_override != null ? "rgba(34,211,238,0.5)" : "var(--pk-border)" }} title="Take this card at a different trade rate than the lot default">
                            <input type="number" inputMode="numeric" min="0" max="100" value={it.pct_override ?? ""} placeholder={String(tradeRate)} onFocus={(e) => e.target.select()}
                              onChange={(e) => updateBuy(it.rid, { pct_override: e.target.value })} data-testid="trade-buy-pct-override"
                              className="w-full bg-transparent outline-none text-xs text-slate-100 font-mono-num text-right" />
                            <span className="text-slate-500 text-xs">%</span>
                            {it.pct_override !== "" && it.pct_override != null && (
                              <button onClick={() => updateBuy(it.rid, { pct_override: "" })} className="text-slate-500 hover:text-rose-400"><X className="h-3 w-3" /></button>
                            )}
                          </div>
                        </label>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-2">Credit</span>
                        <span className="font-mono-num text-sm text-emerald-400" data-testid="trade-buy-credit">{fmt(creditUnit(it) * (Number(it.qty) || 0))}</span>
                        {it.pct_override !== "" && it.pct_override != null && Number(it.pct_override) > 0 && (
                          <div className="text-[10px] text-cyan-400/80" data-testid="trade-buy-pct-badge">@ {Number(it.pct_override)}%</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="px-5 py-3 border-t space-y-2" style={{ borderColor: "var(--pk-border)" }}>
            <label className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-300">Override trade value <span className="text-[11px] text-slate-500">(barter)</span></span>
              <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg border w-32 focus-within:ring-2 focus-within:ring-emerald-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} title="Negotiated flat trade-in credit; overrides the sum">
                <span className="text-slate-500 text-sm">$</span>
                <input type="number" inputMode="decimal" min="0" value={overrideTotal} placeholder={computedTradeIn.toFixed(2)} onFocus={(e) => e.target.select()}
                  onChange={(e) => setOverrideTotal(e.target.value)} data-testid="trade-override-total"
                  className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-right" />
                {overrideVal != null && <button onClick={() => setOverrideTotal("")} data-testid="trade-override-total-clear" className="text-slate-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>}
              </div>
            </label>
            {overrideVal != null && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Computed credit</span><span className="font-mono-num line-through">{fmt(computedTradeIn)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300 font-semibold flex items-center gap-2">Trade-in credit{overrideVal != null && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">flat</span>}</span>
              <span className="font-mono-num text-lg font-bold text-emerald-400" data-testid="trade-in-total">{fmt(tradeIn)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="mt-6 rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
          <div className="flex items-center justify-center gap-3 text-sm">
            <span className="text-amber-400 font-mono-num font-bold">{fmt(ourTotal)}</span>
            <span className="text-slate-500">our cards</span>
            <ArrowRight className="h-4 w-4 text-slate-600" />
            <span className="text-emerald-400 font-mono-num font-bold">{fmt(tradeIn)}</span>
            <span className="text-slate-500">credit</span>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
              {payOverrideVal != null ? "Customer pays (override)" : difference > 0 ? "Customer pays" : difference < 0 ? (payoutOverrideVal != null ? "Cash paid to customer (override)" : "Cash paid to customer") : "Even trade"}
            </div>
            <div className={`font-mono-num text-3xl font-extrabold ${difference < 0 ? "text-amber-400" : (payOverrideVal != null ? payOverrideVal > 0 : difference > 0) ? "text-cyan-400" : "text-slate-300"}`} data-testid="trade-difference">
              {fmt(payOverrideVal != null ? payOverrideVal : difference > 0 ? customerPays : difference < 0 ? cashToCustomer : 0)}
            </div>
            {difference < 0 && (
              <>
                <div className="text-[11px] text-amber-400/80 mt-0.5">paid in cash — no store credit</div>
                <div className="mt-2 flex items-center justify-center gap-1.5">
                  <span className="text-[11px] text-slate-500">Override payout $</span>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg border w-24 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: payoutOverrideVal != null ? "rgba(245,158,11,0.5)" : "var(--pk-border)" }} title="Override the cash we pay out to the customer (barter)">
                    <input type="number" inputMode="decimal" min="0" value={overridePayout} placeholder={roundCash(-difference).toFixed(2)} onFocus={(e) => e.target.select()}
                      onChange={(e) => setOverridePayout(e.target.value)} data-testid="trade-override-payout"
                      className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-right" />
                    {payoutOverrideVal != null && <button onClick={() => setOverridePayout("")} data-testid="trade-override-payout-clear" className="text-slate-500 hover:text-rose-400"><X className="h-3 w-3" /></button>}
                  </div>
                </div>
              </>
            )}
            {difference >= 0 && (
              <div className="mt-2 flex items-center justify-center gap-1.5">
                <span className="text-[11px] text-slate-500">Override pays $</span>
                <div className="flex items-center gap-1 px-2 py-1 rounded-lg border w-24 focus-within:ring-2 focus-within:ring-cyan-500/50" style={{ background: "var(--pk-card)", borderColor: payOverrideVal != null ? "rgba(34,211,238,0.5)" : "var(--pk-border)" }} title="Override the final cash the customer pays">
                  <input type="number" inputMode="decimal" min="0" value={overridePay} placeholder={(difference > 0 ? customerPays : 0).toFixed(2)} onFocus={(e) => e.target.select()}
                    onChange={(e) => setOverridePay(e.target.value)} data-testid="trade-override-pay"
                    className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-right" />
                  {payOverrideVal != null && <button onClick={() => setOverridePay("")} data-testid="trade-override-pay-clear" className="text-slate-500 hover:text-rose-400"><X className="h-3 w-3" /></button>}
                </div>
              </div>
            )}
          </div>
          <button onClick={onComplete} disabled={submitting || (sellCart.length === 0 && buyItems.length === 0)} data-testid="trade-complete-button"
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-500 text-[#04121a] font-bold text-sm hover:bg-cyan-400 disabled:opacity-50 transition-colors">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />} Complete Trade
          </button>
        </div>
      </div>

      {checkoutOpen && (
        <CheckoutModal total={customerPays} paymentQrs={paymentQrs} idPrefix="trade"
          title="Customer Payment" dueLabel="Customer pays"
          onClose={() => setCheckoutOpen(false)} onConfirm={(method, app) => runTrade(method, app)} />
      )}
      <QrScannerModal open={sellScanOpen} onClose={() => setSellScanOpen(false)} onToken={onSellToken}
        title="Scan cards to send out" hint="Scan each card's QR sticker to add it to our cards out." />
      <SlabModal open={buySlabOpen} onClose={() => setBuySlabOpen(false)} onCapture={addSlabBuy}
        title="Take in a Slab" captureLabel="Add to Trade" />
    </div>
  );
}
